import { FC, useState } from "react";
import { Icon } from "@/components/Common/Icon";
import { Button } from "@/components/Base/Button";
import { Checkbox } from "@/components/Base/Checkbox";
import { ColumnConfig } from "../types";
import { getColumnDisplayLabel } from "../hooks/useEnhancedColumns";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ColumnConfigurationPanelProps {
  columns: ColumnConfig[];
  hiddenColumnIds: string[];
  columnOrder: string[];
  onUpdateVisibility: (columnId: string, visible: boolean) => void;
  onReorderColumns: (newOrder: string[]) => void;
  onResetToDefaults: () => void;
}

interface SortableColumnItemProps {
  column: ColumnConfig;
  isVisible: boolean;
  isFixed: boolean;
  onToggleVisibility: (columnId: string, visible: boolean) => void;
}

const SortableColumnItem: FC<SortableColumnItemProps> = ({
  column,
  isVisible,
  isFixed,
  onToggleVisibility,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const displayLabel = getColumnDisplayLabel(column);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded border p-2 ${
        isDragging ? "border-primary" : "border-border"
      } bg-background`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <Icon name="gripVertical" className="h-4 w-4" />
      </div>

      {/* Visibility Checkbox */}
      <Checkbox
        checked={isVisible}
        onCheckedChange={(checked) =>
          onToggleVisibility(column.id, Boolean(checked))
        }
        disabled={isFixed}
        className="border-primary data-[state=checked]:bg-primary"
      />

      {/* Column Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary">
            {displayLabel}
          </span>
          {isFixed && (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Required
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const ColumnConfigurationPanel: FC<ColumnConfigurationPanelProps> = ({
  columns,
  hiddenColumnIds,
  columnOrder,
  onUpdateVisibility,
  onReorderColumns,
  onResetToDefaults,
}) => {
  // Start expanded when used in a popover for better UX
  const [isCollapsed, setIsCollapsed] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      onReorderColumns(newOrder);
    }
  };

  const orderedColumns = columnOrder
    .map((id) => columns.find((col) => col.id === id))
    .filter((col): col is ColumnConfig => col !== undefined);

  const visibleCount = orderedColumns.filter(
    (col) => col.visible && !hiddenColumnIds.includes(col.id)
  ).length;

  const totalCount = orderedColumns.length;

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-6 w-6 p-0"
          >
            <Icon
              name={isCollapsed ? "chevronRight" : "chevronDown"}
              className="h-4 w-4"
            />
          </Button>
          <h3 className="text-sm font-medium text-primary">
            Column Configuration
          </h3>
          <span className="text-xs text-muted-foreground">
            ({visibleCount} of {totalCount} visible)
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onResetToDefaults}
            className="h-7 text-xs"
          >
            <Icon name="rotateCcw" className="mr-1 h-3 w-3" />
            Reset
          </Button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-3">
          <div className="max-h-80 space-y-2 overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columnOrder}
                strategy={verticalListSortingStrategy}
              >
                {orderedColumns.map((column) => {
                  const isVisible =
                    column.visible && !hiddenColumnIds.includes(column.id);
                  return (
                    <SortableColumnItem
                      key={column.id}
                      column={column}
                      isVisible={isVisible}
                      isFixed={column.fixed}
                      onToggleVisibility={onUpdateVisibility}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>

          {/* Help Text */}
          <div className="mt-3 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">
              • Drag columns to reorder them • Uncheck to hide columns (required
              columns cannot be hidden) • Use Reset to restore default column
              settings
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
